import { Cart } from "@bluebottlecoffee/design-system/components";
import {
  CartLineItem,
  Result,
  SubscriptionInterval,
} from "@bluebottlecoffee/design-system/components/lib/types";
import { GiftCardDetails } from "@bluebottlecoffee/design-system/components/GiftCard/GiftCardForm";
import {
  useAnalytics,
  useCart,
  useCheckout,
  useTrackingIdentifiers,
} from "@chordcommerce/react-autonomy";
import { loadStripe } from "@stripe/stripe-js";
import { Currency } from "dinero.js";
import { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { CanonicalHead } from "../../../components/CanonicalHead";
import { useIsClient } from "../../../lib/hooks/use-is-client";
import { parseCookie } from "../../../lib/utils/parse-cookie";
import useVariantPrice from "../../../lib/chord/hooks/components/use-variant-price";
import emailIsValid from "../../../lib/utils/strings/email-is-valid";
import { LayoutWrapper } from "../../../components/LayoutWrapper";
import { AlgoliaRecommendProduct } from "../../../lib/algolia/types";
import { Cart as CartSchema } from "../../../lib/sanity-schema";
import {
  CartBaseProps,
  CartBasePropsWithSlug,
} from "../../../lib/sanity/shared";

import {
  toCartProps,
  toFreeShippingBarProps,
  toGiftMessageFormProps,
  toLineItemsProps,
  toOrderSummaryProps,
  toProductRecsCopyProps,
  toProductRecsProps,
  toRemovePromoAlertProps,
} from "../../../lib/transformers/cart";
import {
  cartVariants,
  discountTotal,
  getDefaultStaticPaths,
  getDefaultStaticPropsMiniCart,
  getIsClientSide,
  getPromoLabels,
  getPromoWithCode,
  isFreeStandardShipping,
  shouldRenderFreeShippingBar,
} from "../../../lib/utils";
import {
  SourceProductRec,
  transformProductRecsToShopCards,
} from "../../../lib/transformers/product-recs";

import { isFeatureEnabled } from "../../../lib/utils/is-feature-enabled";
import {
  getRecommendedProducts,
  productRecsWithBestValueVariant,
} from "../../../lib/utils/product-recs/product-recs";
import { prepaidSubOptions } from "../../../lib/chord/services/oms/prepaid-sub-options";

export const getStaticProps: GetStaticProps<CartBaseProps> = async (
  context,
) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCart(context);

  return {
    props: {
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPaths;

function validate(data: CartSchema) {
  if (!data.heading) {
    throw Error("Cart page must have a heading.");
  }
}

type PageData = CartBasePropsWithSlug & {
  staticProductRecs: SourceProductRec[];
};

const CartPage: FunctionComponent<PageData> = ({
  subscribableProducts,
  ...cartLayoutWrapperProps
}) => {
  const { trackProductClicked, trackCartViewed } = useAnalytics();
  const { writeTrackingIdentifiers } = useTrackingIdentifiers();
  const {
    region,
    lang,
    cart: cartCopy,
    giftCardFormCopy,
    productRecs: staticProductRecs,
    navAndCartInfoBanner,
  } = cartLayoutWrapperProps;
  validate(cartCopy);
  const [promoSuccess, setPromoSuccess] = useState<boolean>(true);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [promoErrorMessage, setPromoErrorMessage] = useState<string>(
    cartCopy.orderSummary.promoCopy.errorMessage[lang],
  );
  const [loadingCart, setLoadingCart] = useState<boolean>(false);
  // For now, we are simply initializing with staticProductRecs from Sanity.
  const [productRecs, setProductRecs] = useState<
    SourceProductRec[] | AlgoliaRecommendProduct[]
  >(staticProductRecs.slice(0, 3));
  const [cartLoaded, setCartLoaded] = useState(false);
  const {
    addToCart,
    addSubscription,
    cart,
    isFetching,
    loadCart,
    removeFromCart,
    modifyLineItem,
    addPromoCode,
    removePromoCode,
    modifyCart,
    modifyGiftCards,
  } = useCart();

  const router = useRouter();

  const isClient = useIsClient();

  useEffect(() => {
    const error = new URLSearchParams(window.location.search).get("error");
    if (error) {
      setCheckoutError(error);
    }
  }, []);

  useEffect(() => {
    setLoadingCart(true);
    loadCart()
      .then(() => {
        setCartLoaded(true);
        setLoadingCart(false);
      })
      .then(() => trackCartViewed())
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
  }, [loadCart]);

  useEffect(() => {
    if (isFetching) {
      setLoadingCart(true);
    } else {
      setLoadingCart(false);
    }
  }, [isFetching]);

  const handleModifyQuantity = useCallback(
    async (lineItemId, quantity) => {
      setLoadingCart(true);
      await modifyLineItem({ lineItemId, attributes: { quantity } });
      setLoadingCart(false);
    },
    [modifyLineItem],
  );

  const handleRemoveFromCart = async (lineItemId: number) => {
    setLoadingCart(true);
    try {
      await removeFromCart({ lineItemId });
      setLoadingCart(false);
      return {
        success: true,
        message: cartCopy.removeSuccessMessage[lang],
      };
    } catch (error) {
      setLoadingCart(false);
      console.error(error.message);
      return {
        success: false,
        message: error.message,
      };
    }
  };

  const onAddPromoCodeSubmit = async (promoCode: string) => {
    try {
      setLoadingCart(true);
      await addPromoCode({ promoCode });
      setLoadingCart(false);
      setPromoSuccess(true);
      setPromoErrorMessage("");
    } catch (error) {
      setPromoSuccess(false);
      setPromoErrorMessage(error.message);
      console.error(error);
    }
  };

  const handleRemovePromoCode = async (promoCode: string) => {
    try {
      setLoadingCart(true);
      await removePromoCode({ promoCode });
      setLoadingCart(false);
      setPromoSuccess(true);
      setPromoErrorMessage("");
    } catch (error) {
      console.error(error);
    }
  };

  const handleModifyGiftCards = async (options: {
    giftCardsDetails: GiftCardDetails[];
  }) => {
    await modifyGiftCards({
      giftCards: {
        giftCardsDetails: options.giftCardsDetails.map((detail) => ({
          id: detail.id.toString(),
          recipientEmail: detail.recipientEmail,
          recipientName: detail.recipientName,
          giftMessage: detail.giftMessage,
          purchaserName: detail.purchaserName,
          sendEmailAt: detail.sendEmailAt,
        })),
      },
    });
  };

  const handleModifyCart = useCallback(
    async (lineItem: CartLineItem, intervalId) => {
      setLoadingCart(true);

      const {
        variant: { sku, subscriptionOptions },
        id,
        quantity,
        prePaidSubscription,
      } = lineItem;

      await removeFromCart({ lineItemId: id });

      const { options, intervals } = subscriptionOptions;

      const { length, unit, _id } = intervals.find(
        (interval) => interval._id === intervalId,
      );

      const subscriptionQuantity =
        options?.find((opt) => opt.quantityLabel.quantity === quantity) ??
        options[options.length - 1];

      try {
        if (_id === "one-time-purchase") {
          await addToCart({
            sku,
            quantity,
          });
        } else if (prePaidSubscription) {
          await addToCart({
            sku,
            quantity,
            // @ts-ignore Chord type mismatch
            options: prepaidSubOptions({
              sku,
              interval: { length, unit },
              installmentCount: prePaidSubscription.installmentCount,
            }),
          });
        } else {
          await addSubscription({
            sku,
            quantity: subscriptionQuantity.quantityLabel.quantity,
            interval: {
              length,
              unit,
            },
            endDate: null,
          });
        }
      } catch (e) {
        loadCart();
        setLoadingCart(false);
        throw new Error(e);
      } finally {
        setLoadingCart(false);
      }
    },
    [modifyCart],
  );

  const onRemoveGiftMessage = async () => {
    try {
      await modifyCart({
        attributes: {
          // @ts-ignore
          giftNoteAttributes: {
            from: "",
            recipient: "",
            note: "",
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddGiftMessage = async (
    to: string,
    from: string,
    giftMessage?: string,
  ) => {
    try {
      await modifyCart({
        attributes: {
          // @ts-ignore
          giftNoteAttributes: {
            from,
            recipient: to,
            note: giftMessage,
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Add Iterable campaign and template ID to Order metadata if values
   * are stored in cookies and castable to number
   *
   * IDs previously set on the Order metadata are removed if the relevant
   * cookies either a) have expired since the last time the method was
   * invoked, or b) no longer evaluate to a number.
   *
   * @returns { Promise<void> }
   */
  async function addOrRemoveCampaignMetadata(): Promise<void> {
    const {
      iterableEmailCampaignIdRevenueAttributable,
      iterableEmailTemplateIdRevenueAttributable,
    } = parseCookie();

    try {
      // A metadata key is removed by setting its value to an empty string:
      // https://chord.stoplight.io/docs/chord-oms/e8bbbcd029277-update-order
      await modifyCart({
        attributes: {
          metadata: {
            campaignId: +iterableEmailCampaignIdRevenueAttributable
              ? +iterableEmailCampaignIdRevenueAttributable
              : "",
            templateId: +iterableEmailTemplateIdRevenueAttributable
              ? +iterableEmailTemplateIdRevenueAttributable
              : "",
          },
        },
      });
    } catch (error) {
      console.error(
        "Failed to modify Iterable campaign metadata on Order:",
        error,
      );
    }
  }

  // remove after checkout is fully migrated to embedded checkout
  const { prepareCheckout } = useCheckout();

  const onCheckoutSubmit = async () => {
    await writeTrackingIdentifiers();
    await addOrRemoveCampaignMetadata();
    if (isFeatureEnabled(process.env.NEXT_PUBLIC_ENABLE_EMBEDDED_CHECKOUT)) {
      router.push(`/${region}/${lang}/checkout`);
    } else {
      setCheckoutError(null);
      await prepareCheckout()
        .then(async (checkoutCart) => {
          // @ts-ignore
          if (checkoutCart.checkoutUrl && getIsClientSide()) {
            // @ts-ignore
            window.location = checkoutCart.checkoutUrl;
          } else {
            const stripe = await loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);
            stripe.redirectToCheckout({
              sessionId: checkoutCart.checkoutSessionId,
            });
          }
        })
        .catch((err) => {
          console.error(err);
          setCheckoutError(err.errors ? err.errors[0] : err.message);
        });
    }
  };

  function isCartEmpty(): boolean {
    return cart.lineItems?.length === 0;
  }

  useEffect(() => {
    const recommendEnabled = isFeatureEnabled(
      process.env.NEXT_PUBLIC_ENABLE_ALGOLIA_RECOMMEND,
    );
    if (recommendEnabled && cartLoaded && !isCartEmpty()) {
      getRecommendedProducts({
        cart,
        lang,
        setProductRecs,
      });
    }
  }, [cartLoaded, cart.lineItems]);

  const transformedGiftNote =
    cart.giftNote?.from && cart.giftNote.recipient
      ? {
          to: cart.giftNote.recipient,
          from: cart.giftNote.from,
          giftMessage: cart.giftNote.note,
        }
      : null;

  const cartProps = toCartProps(cartCopy, giftCardFormCopy, region, lang);

  const handleAddToCardProductRecs = async (
    sku: string,
    quantity: number,
    interval: SubscriptionInterval,
  ): Promise<Result> => {
    try {
      if (interval) {
        await addSubscription({ sku, quantity, interval, endDate: null });
      } else {
        await addToCart({ sku, quantity });
      }
      return {
        success: true,
        message: "Success.",
      };
    } catch (e) {
      loadCart();
      return {
        success: false,
        message: e?.message ?? "Sorry, something went wrong.",
      };
    }
  };

  const pageTitle = checkoutError
    ? `Blue Bottle Coffee | ${cartCopy.heading[lang]} Error: ${checkoutError}`
    : `Blue Bottle Coffee | ${cartCopy.heading[lang]}`;

  return (
    <>
      <CanonicalHead region={region} lang={lang} slug="cart" />
      {isClient && (
        <LayoutWrapper
          {...cartLayoutWrapperProps}
          pageTitle={pageTitle}
          // NavAndCartInfoBanner message renders in Cart component.
          // Undefined here to avoid duplicate message on the cart page only.
          navAndCartInfoBanner={undefined}
        >
          <Cart
            {...cartProps}
            cartLineItemsProps={{
              ...cartProps.cartLineItemsProps,
              removeFromCart: handleRemoveFromCart,
              modifyQuantity: handleModifyQuantity,
              useVariantPrice,
              modifyGiftCards: handleModifyGiftCards,
              modifyCart: handleModifyCart,
              cartLineItems: cartVariants({
                data: cart,
                dialect: { region, lang },
                subscribableProducts,
                oneTimeLabel: cartCopy.oneTimePurchase[lang],
              }),
              giftCardFormCopy,
              emailIsValid,
              loadingCart,
            }}
            infoBanner={
              navAndCartInfoBanner?.showInfoBanners &&
              navAndCartInfoBanner.text[lang]
            }
            renderFreeShippingBar={shouldRenderFreeShippingBar(cart)}
            freeShippingBarProps={{
              ...toFreeShippingBarProps(cartCopy.freeShippingBar, lang),
              estimatedTotal: {
                amount: Math.round(
                  (Number(cart.itemTotal) + Number(cart.adjustmentTotal)) * 100,
                ),
                currency: cart.currency as Currency,
              },
              freeShipPromoCode: isFreeStandardShipping(cart),
            }}
            orderSummaryProps={{
              ...toOrderSummaryProps(cartCopy.orderSummary, lang),
              lineItems: {
                ...toLineItemsProps(cartCopy.orderSummary.lineItems, lang),
                credit:
                  // cart.totalApplicableStoreCredit !== "0.0"
                  //   ? cart.displayTotalApplicableStoreCredit
                  Number(cart.totalApplicableStoreCredit) !== 0.0
                    ? cart.displayTotalApplicableStoreCredit
                    : undefined,
                currency: cart.currency as Currency,
                discountTotal: discountTotal(cart),
                estimatedTotalCost: cart.displayOrderTotalAfterStoreCredit
                  ? cart.displayOrderTotalAfterStoreCredit
                  : undefined,
                freeShipping: isFreeStandardShipping(cart),
                grossSubtotal: cart.displayItemTotal
                  ? cart.displayItemTotal
                  : cartCopy.orderSummary.lineItems.tbd[lang],
                promoLabels: getPromoLabels(cart),
                shippingCost:
                  cart.shipTotal === "0.0" ? undefined : cart.displayShipTotal,
                variantsTotal: cart.totalQuantity
                  ? cart.totalQuantity
                  : undefined,
              },
              giftMessageForm: {
                ...toGiftMessageFormProps(
                  cartCopy.orderSummary.giftMessageCopy,
                  lang,
                ),
                giftMessageProp: transformedGiftNote,
                removeGiftMessage: onRemoveGiftMessage,
                addGiftMessage: onAddGiftMessage,
              },
              promoForm: {
                error: !promoSuccess,
                promoCode: getPromoWithCode(cart),
                promoCopy: {
                  add: cartCopy.orderSummary.promoCopy.add[lang],
                  apply: cartCopy.orderSummary.promoCopy.apply[lang],
                  placeholder:
                    cartCopy.orderSummary.promoCopy.placeholder[lang],
                  errorMessage: promoErrorMessage,
                },
                ...toRemovePromoAlertProps(
                  cartCopy.orderSummary.promoCopy,
                  lang,
                ),
                removePromoCode: handleRemovePromoCode,
                addPromoCode: onAddPromoCodeSubmit,
              },
              /* TODO
               * If the subscription agreement feature is ever requested, define
               * subsPresent to render the checkbox.
               */
              subsPresent: false,
              ctaClick: onCheckoutSubmit,
              checkoutError,
            }}
            productRecsProps={{
              addToCartClick: handleAddToCardProductRecs,
              emptyCart: isCartEmpty(),
              productRecs: toProductRecsProps(
                productRecs,
                region,
                lang,
                cart.currency as Currency,
              ),
              productRecShopCards: transformProductRecsToShopCards(
                productRecsWithBestValueVariant(productRecs, lang),
                lang,
                region,
                (product) => trackProductClicked(product),
              ),
              ...toProductRecsCopyProps(cartCopy, lang),
            }}
          />
        </LayoutWrapper>
      )}
    </>
  );
};

export default CartPage;
